import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  UserCheck,
  Users,
  Search,
  BookOpen,
  ShieldCheck,
  ChevronRight,
  Sparkles,
  SlidersHorizontal,
  RotateCcw,
  Globe,
  Library,
} from 'lucide-react';
import { api } from '../../../shared/api/api.js';
import { LazyBookCover, LazyAvatar, SeoHead, createCollectionPageSchema, PaginationNav } from '../../../shared/components/index.js';
import { slugify } from '../../../shared/utils/stringUtils.js';
import { CustomSelect } from '../../books/components/book-form/CustomSelect.js';

interface ShowcaseDirectoryViewProps {
  onSelectShowcase: (type: 'penulis' | 'penerbit' | 'organisasi', identifier: string) => void;
}

export const ShowcaseDirectoryView: React.FC<ShowcaseDirectoryViewProps> = ({ onSelectShowcase }) => {
  const [activeTab, setActiveTab] = useState<'penerbit' | 'penulis' | 'organisasi'>(() => {
    if (typeof window === 'undefined') return 'penerbit';
    const tabParam = new URLSearchParams(window.location.search).get('tab');
    if (tabParam === 'organisasi' || tabParam === 'komunitas') return 'organisasi';
    if (tabParam === 'penulis') return 'penulis';
    return 'penerbit';
  });

  const [searchQuery, setSearchQuery] = useState(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('q') || '';
  });

  const [verificationFilter, setVerificationFilter] = useState<string>(() => {
    if (typeof window === 'undefined') return 'semua';
    return new URLSearchParams(window.location.search).get('verifikasi') || 'semua';
  });

  const [minBooksFilter, setMinBooksFilter] = useState<string>(() => {
    if (typeof window === 'undefined') return 'semua';
    return new URLSearchParams(window.location.search).get('min_buku') || 'semua';
  });

  const [sortOrder, setSortOrder] = useState<string>(() => {
    if (typeof window === 'undefined') return 'nama-asc';
    return new URLSearchParams(window.location.search).get('sort') || 'nama-asc';
  });

  const [showFilters, setShowFilters] = useState(() => {
    if (typeof window === 'undefined') return false;
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.has('verifikasi') || searchParams.has('min_buku') || searchParams.has('sort');
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(9);

  const { data, isLoading } = useQuery({
    queryKey: ['showcase-directories'],
    queryFn: () => api.getShowcaseDirectories(),
    staleTime: 1000 * 60 * 5,
  });

  const publishers = data?.publishers || [];
  const authors = data?.authors || [];
  const organizations = data?.organizations || [];

  const currentTabCount = activeTab === 'penerbit' 
    ? publishers.length 
    : activeTab === 'organisasi' 
    ? organizations.length 
    : authors.length;

  const verificationOptions = useMemo(() => [
    { value: 'semua', label: `Semua Status (${currentTabCount})` },
    { value: 'terverifikasi', label: 'Hanya Terverifikasi' },
    { value: 'belum_terverifikasi', label: 'Belum Terverifikasi' },
  ], [currentTabCount]);

  const minBooksOptions = useMemo(() => [
    { value: 'semua', label: 'Semua Jumlah Buku' },
    { value: '1', label: 'Minimal 1 Buku' },
    { value: '5', label: 'Minimal 5 Buku' },
    { value: '10', label: 'Minimal 10 Buku' },
    { value: '25', label: 'Minimal 25 Buku' },
  ], []);

  const sortOptions = useMemo(() => [
    { value: 'nama-asc', label: 'Nama (A-Z)' },
    { value: 'nama-desc', label: 'Nama (Z-A)' },
    { value: 'buku-desc', label: 'Buku Terbit Terbanyak' },
    { value: 'buku-asc', label: 'Buku Terbit Tersedikit' },
  ], []);

  // Synchronize state with URL query parameters for sharing
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);

    url.searchParams.set('tab', activeTab);

    if (searchQuery.trim()) {
      url.searchParams.set('q', searchQuery);
    } else {
      url.searchParams.delete('q');
    }

    if (verificationFilter && verificationFilter !== 'semua') {
      url.searchParams.set('verifikasi', verificationFilter);
    } else {
      url.searchParams.delete('verifikasi');
    }

    if (minBooksFilter && minBooksFilter !== 'semua') {
      url.searchParams.set('min_buku', minBooksFilter);
    } else {
      url.searchParams.delete('min_buku');
    }

    if (sortOrder && sortOrder !== 'nama-asc') {
      url.searchParams.set('sort', sortOrder);
    } else {
      url.searchParams.delete('sort');
    }

    window.history.replaceState({}, '', url.pathname + url.search);
  }, [activeTab, searchQuery, verificationFilter, minBooksFilter, sortOrder]);

  const handleTabChange = (tab: 'penerbit' | 'penulis' | 'organisasi') => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const directoryStructuredData = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://uibn.id';
    const items = activeTab === 'penerbit'
      ? publishers.map((p) => ({
          name: p.name,
          url: `${origin}/penerbit/${slugify(p.name)}`,
          description: `Penerbit terverifikasi dengan kode ${p.code || 'UIBN'}`,
        }))
      : activeTab === 'organisasi'
      ? organizations.map((o) => ({
          name: o.name,
          url: `${origin}/organisasi/${slugify(o.name)}`,
          description: `Organisasi & Komunitas terdaftar di katalog UIBN`,
        }))
      : authors.map((a) => ({
          name: a.name,
          url: `${origin}/penulis/${slugify(a.name)}`,
          description: `Penulis karya terdaftar di katalog UIBN`,
        }));

    return createCollectionPageSchema(
      'Direktori Penerbit, Penulis & Komunitas Terdaftar | UIBN',
      'Jelajahi profil publik, katalog buku terbit, dan karya literasi dari seluruh penerbit resmi, penulis mandiri, serta organisasi/komunitas terdaftar di Platform UIBN.',
      `${origin}/direktori`,
      items
    );
  }, [activeTab, publishers, authors, organizations]);

  const filteredPublishers = useMemo(() => {
    let result = publishers.filter((p) => {
      const matchesSearch =
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.code && p.code.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesVerification =
        verificationFilter === 'semua' ||
        (verificationFilter === 'terverifikasi' && p.verified) ||
        (verificationFilter === 'belum_terverifikasi' && !p.verified);

      const minBooks = minBooksFilter === 'semua' ? 0 : parseInt(minBooksFilter, 10);
      const matchesMinBooks = p.bookCount >= minBooks;

      return matchesSearch && matchesVerification && matchesMinBooks;
    });

    // Sorting
    if (sortOrder === 'nama-asc') {
      result.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortOrder === 'nama-desc') {
      result.sort((a, b) => b.name.localeCompare(a.name));
    } else if (sortOrder === 'buku-desc') {
      result.sort((a, b) => b.bookCount - a.bookCount);
    } else if (sortOrder === 'buku-asc') {
      result.sort((a, b) => a.bookCount - b.bookCount);
    }

    return result;
  }, [publishers, searchQuery, verificationFilter, minBooksFilter, sortOrder]);

  const filteredAuthors = useMemo(() => {
    let result = authors.filter((a) => {
      const matchesSearch = a.name.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesVerification =
        verificationFilter === 'semua' ||
        (verificationFilter === 'terverifikasi' && a.verified) ||
        (verificationFilter === 'belum_terverifikasi' && !a.verified);

      const minBooks = minBooksFilter === 'semua' ? 0 : parseInt(minBooksFilter, 10);
      const matchesMinBooks = a.bookCount >= minBooks;

      return matchesSearch && matchesVerification && matchesMinBooks;
    });

    // Sorting
    if (sortOrder === 'nama-asc') {
      result.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortOrder === 'nama-desc') {
      result.sort((a, b) => b.name.localeCompare(a.name));
    } else if (sortOrder === 'buku-desc') {
      result.sort((a, b) => b.bookCount - a.bookCount);
    } else if (sortOrder === 'buku-asc') {
      result.sort((a, b) => a.bookCount - b.bookCount);
    }

    return result;
  }, [authors, searchQuery, verificationFilter, minBooksFilter, sortOrder]);

  const filteredOrganizations = useMemo(() => {
    let result = organizations.filter((o) => {
      const matchesSearch =
        o.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (o.code && o.code.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesVerification =
        verificationFilter === 'semua' ||
        (verificationFilter === 'terverifikasi' && o.verified) ||
        (verificationFilter === 'belum_terverifikasi' && !o.verified);

      const minBooks = minBooksFilter === 'semua' ? 0 : parseInt(minBooksFilter, 10);
      const matchesMinBooks = o.bookCount >= minBooks;

      return matchesSearch && matchesVerification && matchesMinBooks;
    });

    // Sorting
    if (sortOrder === 'nama-asc') {
      result.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortOrder === 'nama-desc') {
      result.sort((a, b) => b.name.localeCompare(a.name));
    } else if (sortOrder === 'buku-desc') {
      result.sort((a, b) => b.bookCount - a.bookCount);
    } else if (sortOrder === 'buku-asc') {
      result.sort((a, b) => a.bookCount - b.bookCount);
    }

    return result;
  }, [organizations, searchQuery, verificationFilter, minBooksFilter, sortOrder]);

  const activeItems = activeTab === 'penerbit' 
    ? filteredPublishers 
    : activeTab === 'organisasi' 
    ? filteredOrganizations 
    : filteredAuthors;

  const totalPages = Math.max(1, Math.ceil(activeItems.length / itemsPerPage));

  const paginatedPublishers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredPublishers.slice(start, start + itemsPerPage);
  }, [filteredPublishers, currentPage, itemsPerPage]);

  const paginatedAuthors = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAuthors.slice(start, start + itemsPerPage);
  }, [filteredAuthors, currentPage, itemsPerPage]);

  const paginatedOrganizations = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredOrganizations.slice(start, start + itemsPerPage);
  }, [filteredOrganizations, currentPage, itemsPerPage]);

  if (isLoading) {
    return (
      <div className="py-16 flex flex-col items-center justify-center space-y-4">
        <div className="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center animate-spin">
          <BookOpen className="w-6 h-6 text-indigo-600" />
        </div>
        <p className="text-xs font-semibold text-slate-500">Memuat Direktori Publik UIBN...</p>
      </div>
    );
  }

  const tabLabelIndonesian = activeTab === 'organisasi' 
    ? 'organisasi / komunitas' 
    : activeTab === 'penerbit' 
    ? 'penerbit' 
    : 'penulis';

  return (
    <div className="space-y-8 pb-12">
      <SeoHead
        title="Direktori Penerbit, Penulis & Komunitas Terdaftar"
        description="Jelajahi profil publik, katalog buku terbit, dan karya literasi dari seluruh penerbit resmi, penulis mandiri, serta organisasi dan komunitas literasi di Platform UIBN."
        keywords={['direktori penerbit', 'daftar penulis', 'direktori komunitas', 'organisasi literasi', 'katalog buku terbit', 'profil penerbit indonesia', 'UIBN direktori']}
        structuredData={directoryStructuredData}
      />

      {/* Hero Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-indigo-900 via-indigo-950 to-slate-900 text-white rounded-3xl p-6 sm:p-8 shadow-2xl border border-indigo-950">
        <div className="relative z-10 max-w-2xl space-y-3">
          <span className="px-3 py-1 rounded-full text-xs font-bold bg-indigo-500/20 text-indigo-300 border border-indigo-400/30 inline-flex items-center gap-1.5">
            <Library className="w-3.5 h-3.5 text-indigo-400" />
            <span>Direktori Publik UIBN Indonesia</span>
          </span>

          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
            Jelajahi Katalog Penerbit, Penulis & Komunitas
          </h1>

          <p className="text-xs sm:text-sm text-indigo-200/80 leading-relaxed">
            Temukan profil resmi lembaga penerbit, penulis mandiri, dan komunitas literasi beserta koleksi buku digital berkode UIBN yang telah terverifikasi secara publik.
          </p>
        </div>
      </div>

      {/* Directory Controls & Search */}
      <div className="bg-white rounded-3xl border border-slate-200 p-5 sm:p-6 shadow-2xs space-y-4">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
          {/* Toggle Tabs (3 Tabs) */}
          <div className="flex flex-wrap sm:flex-nowrap bg-slate-100 p-1 rounded-2xl border border-slate-200/80 w-full lg:w-auto gap-1">
            <button
              type="button"
              onClick={() => handleTabChange('penerbit')}
              className={`flex-1 px-4 py-2.5 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 cursor-pointer ${
                activeTab === 'penerbit'
                  ? 'bg-white text-indigo-600 shadow-3xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Building2 className="w-4 h-4 text-indigo-600" />
              <span>Penerbit ({publishers.length})</span>
            </button>

            <button
              type="button"
              onClick={() => handleTabChange('penulis')}
              className={`flex-1 px-4 py-2.5 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 cursor-pointer ${
                activeTab === 'penulis'
                  ? 'bg-white text-indigo-600 shadow-3xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <UserCheck className="w-4 h-4 text-indigo-600" />
              <span>Penulis ({authors.length})</span>
            </button>

            <button
              type="button"
              onClick={() => handleTabChange('organisasi')}
              className={`flex-1 px-4 py-2.5 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 cursor-pointer ${
                activeTab === 'organisasi'
                  ? 'bg-white text-amber-700 shadow-3xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Users className="w-4 h-4 text-amber-600" />
              <span>Organisasi / Komunitas ({organizations.length})</span>
            </button>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
            {/* Search Input */}
            <div className="relative flex-1 sm:w-72">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={`Cari nama ${tabLabelIndonesian}...`}
                className="w-full pl-9 pr-3.5 py-2.5 text-xs border border-slate-200 rounded-xl bg-slate-50/50 focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600 transition"
              />
            </div>

            {/* Collapsible Filter Toggle Button */}
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center justify-center gap-1.5 px-3.5 py-2.5 text-xs font-bold rounded-xl border transition cursor-pointer ${
                showFilters || verificationFilter !== 'semua' || minBooksFilter !== 'semua' || sortOrder !== 'nama-asc'
                  ? 'bg-indigo-50 border-indigo-200 text-indigo-600 shadow-3xs'
                  : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              <span>Filter Lanjutan</span>
              {(verificationFilter !== 'semua' || minBooksFilter !== 'semua' || sortOrder !== 'nama-asc') && (
                <span className="w-2 h-2 rounded-full bg-indigo-600"></span>
              )}
            </button>
          </div>
        </div>

        {/* Collapsible Advanced Filters Panel */}
        {showFilters && (
          <div className="p-4 bg-slate-50/80 border border-slate-200/60 rounded-2xl mt-2 space-y-4 animate-in slide-in-from-top-2 duration-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 items-end">
              {/* Status Verifikasi Dropdown */}
              <CustomSelect
                label="Status Verifikasi"
                value={verificationFilter}
                onChange={(val) => {
                  setVerificationFilter(val);
                  setCurrentPage(1);
                }}
                options={verificationOptions}
                className="w-full"
              />

              {/* Jumlah Buku Dropdown */}
              <CustomSelect
                label="Jumlah Buku Terbit"
                value={minBooksFilter}
                onChange={(val) => {
                  setMinBooksFilter(val);
                  setCurrentPage(1);
                }}
                options={minBooksOptions}
                className="w-full"
              />

              {/* Urutkan Dropdown */}
              <CustomSelect
                label="Urutkan Berdasarkan"
                value={sortOrder}
                onChange={(val) => {
                  setSortOrder(val);
                  setCurrentPage(1);
                }}
                options={sortOptions}
                className="w-full"
              />

              {/* Reset Action */}
              <div className="flex items-center justify-between sm:justify-end gap-3 w-full">
                {(verificationFilter !== 'semua' || minBooksFilter !== 'semua' || sortOrder !== 'nama-asc' || searchQuery) ? (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('');
                      setVerificationFilter('semua');
                      setMinBooksFilter('semua');
                      setSortOrder('nama-asc');
                      setCurrentPage(1);
                    }}
                    className="flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-bold text-rose-600 hover:text-rose-700 bg-rose-50 border border-rose-100 hover:bg-rose-100 rounded-xl transition cursor-pointer w-full"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    <span>Reset Filter</span>
                  </button>
                ) : (
                  <span className="text-[10px] text-slate-400 font-medium italic hidden sm:inline text-right w-full">
                    Gunakan opsi di atas untuk menyaring direktori...
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Grid of Publishers */}
      {activeTab === 'penerbit' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5 2xl:gap-6">
            {paginatedPublishers.map((p, idx) => (
              <div
                key={`pub-${p.name}-${idx}`}
                onClick={() => onSelectShowcase('penerbit', p.name)}
                className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-2xs hover:shadow-md hover:border-indigo-300 transition cursor-pointer group flex flex-col justify-between space-y-4"
              >
                <div className="flex items-start gap-4">
                  {p.profileImageUrl ? (
                    <LazyAvatar
                      src={p.profileImageUrl}
                      alt={p.name}
                      size="lg"
                      className="w-14 h-14 rounded-2xl border border-slate-200 shrink-0 shadow-2xs"
                      fallbackIcon={<Building2 className="w-6 h-6 text-indigo-500" />}
                      priority={idx < 6}
                    />
                  ) : (
                    <LazyBookCover
                      coverUrl={p.sampleCover}
                      title={p.name}
                      className="w-14 h-20 rounded-xl"
                      targetWidth={200}
                      priority={idx < 6}
                    />
                  )}

                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-100">
                        Penerbit
                      </span>
                      {p.verified && (
                        <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100 flex items-center gap-0.5">
                          <ShieldCheck className="w-3 h-3 text-emerald-600" />
                          <span>Terverifikasi</span>
                        </span>
                      )}
                    </div>

                    <h3 className="font-bold text-slate-900 text-sm group-hover:text-indigo-600 transition truncate">
                      {p.name}
                    </h3>

                    {p.code && (
                      <p className="text-[11px] font-mono text-slate-400">
                        Kode: <span className="text-slate-700 font-semibold">{p.code}</span>
                      </p>
                    )}
                  </div>
                </div>

                <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 font-semibold">
                  <span>{p.bookCount} Buku Terbit UIBN</span>
                  <span className="text-indigo-600 flex items-center gap-1 font-bold group-hover:translate-x-0.5 transition-transform">
                    <span>Lihat Halaman</span>
                    <ChevronRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            ))}

            {filteredPublishers.length === 0 && (
              <div className="col-span-full py-12 text-center bg-white border border-slate-200 rounded-2xl p-6">
                <p className="text-xs text-slate-500">Tidak ada penerbit ditemukan.</p>
              </div>
            )}
          </div>

          <PaginationNav
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredPublishers.length}
            itemsPerPage={itemsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
            onItemsPerPageChange={(size) => setItemsPerPage(size)}
            itemsPerPageOptions={[6, 9, 12, 24, 48]}
            itemLabel="Penerbit"
          />
        </div>
      )}

      {/* Grid of Authors */}
      {activeTab === 'penulis' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5 2xl:gap-6">
            {paginatedAuthors.map((a, idx) => (
              <div
                key={`auth-${a.name}-${idx}`}
                onClick={() => onSelectShowcase('penulis', a.name)}
                className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-2xs hover:shadow-md hover:border-indigo-300 transition cursor-pointer group flex flex-col justify-between space-y-4"
              >
                <div className="flex items-start gap-4">
                  {a.profileImageUrl ? (
                    <LazyAvatar
                      src={a.profileImageUrl}
                      alt={a.name}
                      size="lg"
                      className="w-14 h-14 rounded-2xl border border-slate-200 shrink-0 shadow-2xs"
                      fallbackIcon={<UserCheck className="w-6 h-6 text-indigo-500" />}
                      priority={idx < 6}
                    />
                  ) : (
                    <LazyBookCover
                      coverUrl={a.sampleCover}
                      title={a.name}
                      className="w-14 h-20 rounded-xl"
                      targetWidth={200}
                      priority={idx < 6}
                    />
                  )}

                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-100">
                        Penulis
                      </span>
                      {a.verified && (
                        <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100 flex items-center gap-0.5">
                          <ShieldCheck className="w-3 h-3 text-emerald-600" />
                          <span>Terverifikasi</span>
                        </span>
                      )}
                    </div>

                    <h3 className="font-bold text-slate-900 text-sm group-hover:text-indigo-600 transition truncate">
                      {a.name}
                    </h3>

                    <p className="text-[11px] text-slate-400">
                      Penulis Terdaftar UIBN
                    </p>
                  </div>
                </div>

                <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 font-semibold">
                  <span>{a.bookCount} Buku Terbit</span>
                  <span className="text-indigo-600 flex items-center gap-1 font-bold group-hover:translate-x-0.5 transition-transform">
                    <span>Lihat Karya</span>
                    <ChevronRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            ))}

            {filteredAuthors.length === 0 && (
              <div className="col-span-full py-12 text-center bg-white border border-slate-200 rounded-2xl p-6">
                <p className="text-xs text-slate-500">Tidak ada penulis ditemukan.</p>
              </div>
            )}
          </div>

          <PaginationNav
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredAuthors.length}
            itemsPerPage={itemsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
            onItemsPerPageChange={(size) => setItemsPerPage(size)}
            itemsPerPageOptions={[6, 9, 12, 24, 48]}
            itemLabel="Penulis"
          />
        </div>
      )}

      {/* Grid of Organizations & Communities */}
      {activeTab === 'organisasi' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5 2xl:gap-6">
            {paginatedOrganizations.map((o, idx) => (
              <div
                key={`org-${o.name}-${idx}`}
                onClick={() => onSelectShowcase('organisasi', o.name)}
                className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-2xs hover:shadow-md hover:border-amber-400 transition cursor-pointer group flex flex-col justify-between space-y-4"
              >
                <div className="flex items-start gap-4">
                  {o.profileImageUrl ? (
                    <LazyAvatar
                      src={o.profileImageUrl}
                      alt={o.name}
                      size="lg"
                      className="w-14 h-14 rounded-2xl border border-slate-200 shrink-0 shadow-2xs"
                      fallbackIcon={<Users className="w-6 h-6 text-amber-600" />}
                      priority={idx < 6}
                    />
                  ) : (
                    <LazyBookCover
                      coverUrl={o.sampleCover}
                      title={o.name}
                      className="w-14 h-20 rounded-xl"
                      targetWidth={200}
                      priority={idx < 6}
                    />
                  )}

                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200">
                        Komunitas / Org
                      </span>
                      {o.verified && (
                        <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100 flex items-center gap-0.5">
                          <ShieldCheck className="w-3 h-3 text-emerald-600" />
                          <span>Terverifikasi</span>
                        </span>
                      )}
                    </div>

                    <h3 className="font-bold text-slate-900 text-sm group-hover:text-amber-700 transition truncate">
                      {o.name}
                    </h3>

                    {o.code && (
                      <p className="text-[11px] font-mono text-slate-400">
                        Kode: <span className="text-slate-700 font-semibold">{o.code}</span>
                      </p>
                    )}
                  </div>
                </div>

                <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 font-semibold">
                  <span>{o.bookCount} Buku Terbit UIBN</span>
                  <span className="text-amber-700 flex items-center gap-1 font-bold group-hover:translate-x-0.5 transition-transform">
                    <span>Lihat Halaman</span>
                    <ChevronRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            ))}

            {filteredOrganizations.length === 0 && (
              <div className="col-span-full py-12 text-center bg-white border border-slate-200 rounded-2xl p-6">
                <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-xs text-slate-500 font-medium">Tidak ada organisasi atau komunitas ditemukan.</p>
                <p className="text-[11px] text-slate-400 mt-1">Daftarkan komunitas literasi Anda untuk menerbitkan buku dengan UIBN.</p>
              </div>
            )}
          </div>

          <PaginationNav
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredOrganizations.length}
            itemsPerPage={itemsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
            onItemsPerPageChange={(size) => setItemsPerPage(size)}
            itemsPerPageOptions={[6, 9, 12, 24, 48]}
            itemLabel="Organisasi & Komunitas"
          />
        </div>
      )}
    </div>
  );
};
